import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, BookOpen, Headphones, Library } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Seo, SITE_URL } from '../components/Seo';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const truncateAtWord = (text, max) => {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  return `${cut.slice(0, cut.lastIndexOf(' ') > 0 ? cut.lastIndexOf(' ') : cut.length)}…`;
};

export default function PublicBookPage() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);


  useEffect(() => {
    const fetchBook = async () => {
      setLoading(true);
      setNotFound(false);
      setError(false);
      try {
        const res = await fetch(`${API_URL}/api/public/book/${slug}`);
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) {
          setError(true);
          return;
        }
        const data = await res.json();
        setBook(data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchBook();
  }, [slug]);

  const canonicalUrl = `${SITE_URL}/book/${slug}`;
  const fallbackSeo = (
    <Seo
      title="Book | NewAudia"
      description="Discover this book on NewAudia, the interactive reading platform where you can read, listen, and ask Audia, your AI reading companion, about any book."
      canonicalUrl={canonicalUrl}
      type="book"
    />
  );

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center" data-testid="public-book-loading">
        {fallbackSeo}
        <Loader2 className="w-10 h-10 animate-spin text-[#00A896]" />
      </div>
    );
  }

  // 404
  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex flex-col items-center justify-center gap-6 px-4" data-testid="public-book-not-found">
        <Seo
          title="Book Not Found | NewAudia"
          description="This book isn't available on NewAudia right now. Browse the NewAudia Public Library to discover books you can read, listen to, and ask Audia about."
          canonicalUrl={canonicalUrl}
          noindex
        />
        <BookOpen className="w-16 h-16 text-slate-600" />
        <h1 className="text-2xl font-bold text-white">Book not found</h1>
        <p className="text-slate-400 text-center max-w-md">
          The book you're looking for doesn't exist or hasn't been indexed yet.
        </p>
        <Link to="/public/library" className="text-[#00A896] hover:underline font-medium" data-testid="public-book-not-found-library-link">
          Browse the library
        </Link>
      </div>
    );
  }

  // Error
  if (error || !book) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex flex-col items-center justify-center gap-6 px-4" data-testid="public-book-error">
        {fallbackSeo}
        <h1 className="text-2xl font-bold text-white">Something went wrong</h1>
        <p className="text-slate-400 text-center max-w-md">
          We couldn't load this book right now. Please try again.
        </p>
        <Button
          onClick={() => window.location.reload()}
          className="bg-[#00A896] hover:bg-[#009080] text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  const bookUrl = book.canonical_url || canonicalUrl;
  const metaDescription = book.description
    ? truncateAtWord(book.description, 155)
    : `Read, listen to, and ask Audia about ${book.title} by ${book.author} on NewAudia, the interactive reading platform.`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: book.title,
    author: { '@type': 'Person', name: book.author },
    url: bookUrl,
    ...(book.seo_publisher_name && { publisher: { '@type': 'Organization', name: book.seo_publisher_name } }),
    ...(book.description && { description: book.description }),
    ...(book.cover_image_url && { image: book.cover_image_url }),
    ...(book.genre && { genre: book.genre }),
  };

  return (
    <>
      <Seo
        title={`${book.title} by ${book.author} | NewAudia`}
        description={metaDescription}
        canonicalUrl={bookUrl}
        image={book.cover_image_url || undefined}
        type="book"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#0B1120]">
        <div className="max-w-2xl mx-auto px-4 py-12 sm:py-20">
          {/* Cover */}
          <div className="flex justify-center mb-8">
            {book.cover_image_url && !coverFailed ? (
              <img
                src={`${API_URL}/api/public/book/${slug}/cover`}
                alt={`Cover of ${book.title}`}
                onError={() => setCoverFailed(true)}
                className="w-48 sm:w-56 rounded-lg shadow-2xl shadow-black/50"
              />
            ) : (
              <div className="w-48 sm:w-56 h-72 sm:h-80 rounded-lg bg-[#1B3A6B]/40 border border-slate-700 flex items-center justify-center">
                <BookOpen className="w-12 h-12 text-slate-500" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-white text-center leading-tight" data-testid="public-book-title">
            {book.title}
          </h1>

          {/* Author */}
          <h2 className="text-lg sm:text-xl text-[#00A896] text-center mt-3 font-medium">
            {book.author}
          </h2>

          {/* Subtitle */}
          {book.subtitle && (
            <p className="text-slate-400 text-center mt-2 italic text-sm sm:text-base">
              {book.subtitle}
            </p>
          )}

          {/* Publisher */}
          {book.seo_publisher_name && (
            <p className="text-slate-500 text-center mt-2 text-sm">
              Published by {book.seo_publisher_name}
            </p>
          )}

          {/* Description */}
          {book.description && (
            <div className="mt-8 px-2">
              <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
                {book.description}
              </p>
            </div>
          )}

          {/* Calls to action */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3" data-testid="public-book-ctas">
            {book.has_sample && book.manuscript_id && (
              <Button asChild size="lg" className="w-full sm:w-auto rounded-full bg-[#00A896] hover:bg-[#009080] text-white font-semibold px-8">
                <Link to={`/sample/${book.manuscript_id}`} data-testid="public-book-sample-cta">
                  <Headphones className="w-5 h-5 mr-2" />
                  Listen to a free sample
                </Link>
              </Button>
            )}
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white px-8">
              <Link to="/public/library" data-testid="public-book-library-cta">
                <Library className="w-5 h-5 mr-2" />
                Browse the library
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
